
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { pt, enUS } from 'date-fns/locale';
import { UseFormReturn } from "react-hook-form";
import type { FormValues } from "../schema/assessmentSchema";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";

interface BasicInfoCardProps {
  form: UseFormReturn<FormValues>;
  students: any[];
}

export const BasicInfoCard = ({ form, students }: BasicInfoCardProps) => {
  const { t } = useTranslations("assessments");
  const { language } = useLanguage();
  const dateLocale = language === 'pt' ? pt : enUS;
  
  return (
    <Card className="dark:bg-gray-800 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="dark:text-white">{t("form.basicInfo")}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6 sm:grid-cols-2">
        <FormField
          control={form.control}
          name="student_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="dark:text-gray-200">{t("form.student")}</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background dark:bg-gray-800 dark:border-gray-700 dark:text-white">
                    <SelectValue placeholder={t("form.selectStudent")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {students?.map((student) => (
                    <SelectItem key={student.id} value={student.id}>
                      {student.first_name} {student.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage className="dark:text-red-400" />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="assessment_date"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel className="dark:text-gray-200">{t("form.assessmentDate")}</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant={"outline"}
                      className="pl-3 text-left font-normal bg-background dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    >
                      {field.value ? (
                        format(field.value, "PPP", { locale: dateLocale })
                      ) : (
                        <span>{t("form.selectDate")}</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 dark:bg-gray-800 dark:border-gray-700" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    disabled={(date) =>
                      date > new Date() || date < new Date("1900-01-01")
                    }
                    initialFocus
                    locale={dateLocale}
                    className="dark:bg-gray-800 dark:text-white"
                  />
                </PopoverContent>
              </Popover>
              <FormMessage className="dark:text-red-400" />
            </FormItem>
          )}
        />
      </CardContent>
    </Card>
  );
};
